import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import { FloatingUIManager } from '../../utils/floating-ui'
import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right'
type TooltipType = 'standard' | 'popover'
type TooltipDensity = 'large' | 'medium' | 'small'
type TooltipState = 'info' | 'warning' | 'error' | 'danger' | 'success'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/tooltip?tab=designer).
 *
 * @slot trigger - Slot para o elemento que aciona a exibição do tooltip.
 * @slot content - Slot para o conteúdo exibido no tooltip.
 */
@Component({
  tag: 'br-tooltip',
  styleUrl: 'tooltip.scss',
  shadow: true,
  formAssociated: true,
})
export class Tooltip {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el!: HTMLBrTooltipElement

  /**
   * Referência ao elemento Internals do componente.
   * O elemento Internals fornece uma interface para acessar e manipular o estado interno do componente.
   * Isso é útil para componentes que precisam se integrar com o sistema de formulários nativo do navegador.
   */
  @AttachInternals() elementInternals!: ElementInternals

  /**
   * Identificador único do tooltip; gerado automaticamente quando omitido.
   */
  @Prop({ reflect: true }) customId: string = Helpers.generateUniqueId()

  /**
   * Posição do tooltip em relação ao acionador.
   */
  @Prop({ reflect: true }) position: TooltipPosition = 'top'

  /**
   * Tipo do tooltip: 'standard' (simples) ou 'popover' (mais rico, com fechamento por interação).
   */
  @Prop({ reflect: true }) type: TooltipType = 'standard'

  /**
   * Densidade do tooltip: controla compacidade do conteúdo.
   */
  @Prop({ reflect: true }) density: TooltipDensity = 'medium'

  /**
   * Estado visual do tooltip: 'info', 'warning', 'error', 'danger' ou 'success'.
   */
  @Prop({ reflect: true }) state: TooltipState = 'info'

  /**
   * Exibe automaticamente sem interação quando true; caso contrário, depende do acionador.
   */
  @Prop({ reflect: true }) isAutoVisible: boolean = false

  /**
   * Atraso em milissegundos antes de exibir.
   */
  @Prop({ reflect: true }) showDelay: number | null = null

  /**
   * Atraso em milissegundos antes de ocultar.
   */
  @Prop({ reflect: true }) hideDelay: number | null = null

  /**
   * Estado interno de visibilidade do tooltip.
   */
  @State() isVisible: boolean = false

  /**
   * Indica se o conteúdo do trigger é focável.
   * Se for false, o wrapper do trigger receberá tabindex="0".
   */
  @State() private hasFocusableTrigger: boolean = false

  /**
   * Watcher para monitorar alterações na propriedade `isVisible`.
   * @param visible boolean
   * @returns void
   */
  @Watch('isVisible')
  handleVisibilityChange(visible: boolean) {
    if (this.contentElement == null) return

    if (visible) {
      this.positionTooltip()
      this.brDidOpen.emit({ id: this.customId, visible: true })
    } else {
      this.clearAutoUpdate?.()
      this.clearAutoUpdate = undefined
      this.brDidClose.emit({ id: this.customId, visible: false })
    }
  }

  /**
   * Emitido quando o tooltip abre.
   */
  @Event({
    eventName: 'brDidOpen',
    composed: true,
    cancelable: false,
    bubbles: true,
  })
  brDidOpen!: EventEmitter<{ id: string; visible: boolean }>

  /**
   * Emitido quando o tooltip fecha.
   */
  @Event({
    eventName: 'brDidClose',
    composed: true,
    cancelable: false,
    bubbles: true,
  })
  brDidClose!: EventEmitter<{ id: string; visible: boolean }>

  /**
   * Exibe o tooltip quando o acionador recebe foco.
   */
  @Listen('focusin')
  handleFocusIn() {
    if (!this.fromPointerDown) {
      this.showContent()
    }
    this.fromPointerDown = false
  }

  /**
   * Oculta o tooltip quando o acionador perde foco.
   */
  @Listen('focusout')
  handleFocusOut() {
    this.hideContent()
  }

  /**
   * Fecha com Escape quando o documento recebe a tecla.
   */
  @Listen('keydown', { target: 'document' })
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.hideContent()
      this.getPositionReferenceElement()?.focus()
    }
  }

  private floatingManager?: FloatingUIManager

  /**
   * Elemento que aciona a exibição do tooltip.
   * Este elemento é referenciado para garantir que o tooltip seja posicionado corretamente em relação a ele.
   */
  private triggerElement: HTMLDivElement | null = null

  /**
   * Trigger externo opcional para posicionamento e interações.
   */
  private externalTriggerElement: HTMLElement | null = null

  /**
   * Elemento que atualmente possui os listeners de interação.
   */
  private interactionTarget: HTMLElement | null = null

  /**
   * Elemento que contém o conteúdo do tooltip.
   * Este elemento é referenciado para garantir que o tooltip seja posicionado corretamente em relação a ele.
   */
  private contentElement: HTMLDivElement | null = null

  /**
   * Elemento que representa a seta do tooltip.
   * Este elemento é utilizado para indicar a direção do tooltip em relação ao elemento acionador.
   */
  private arrowElement: HTMLDivElement | null = null

  /**
   * Função de limpeza para atualizar a posição do tooltip automaticamente.
   * Esta função é chamada quando o tooltip não está mais visível ou quando o componente é desconectado.
   * Isso garante que o tooltip não consuma recursos desnecessários quando não estiver em uso.
   */
  private clearAutoUpdate?: () => void

  /**
   * Tempo limite para exibir o tooltip automaticamente.
   */
  private showTimer: ReturnType<typeof setTimeout> | null = null

  /**
   * Tempo limite para ocultar o tooltip.
   */
  private hideTimer: ReturnType<typeof setTimeout> | null = null

  /**
   * Indica se o tooltip foi acionado por um clique do mouse.
   * Isso é utilizado para evitar conflito com o acionamento por foco.
   */
  private fromPointerDown: boolean = false

  /**
   * Define um trigger externo para o tooltip.
   */
  @Method()
  async setTriggerElement(element: HTMLElement | null) {
    this.removeTriggerInteractions(this.interactionTarget)
    this.externalTriggerElement = element
    this.setupInteractions()

    if (this.isVisible) {
      this.positionTooltip()
    }
  }

  /**
   * Exibe o tooltip programaticamente.
   */
  @Method()
  async showTooltip() {
    this.showContent()
  }

  /**
   * Oculta o tooltip programaticamente.
   */
  @Method()
  async hideTooltip() {
    this.hideContent()
  }

  /**
   * Retorna o elemento de referência atual para posicionamento.
   */
  private getPositionReferenceElement(): HTMLElement | null {
    if (this.externalTriggerElement !== null) {
      return this.externalTriggerElement
    }

    return this.triggerElement
  }

  /**
   * Remove listeners de interação do elemento de trigger atual.
   */
  private removeTriggerInteractions(target: HTMLElement | null) {
    if (target === null) return

    target.removeEventListener('mouseenter', this.showContent)
    target.removeEventListener('mouseleave', this.hideContent)
    target.removeEventListener('pointerdown', this.toggleContent)
  }

  /**
   * Adiciona listeners de interação ao elemento de trigger atual.
   */
  private addTriggerInteractions(target: HTMLElement) {
    if (this.type === 'standard') {
      target.addEventListener('mouseenter', this.showContent)
      target.addEventListener('mouseleave', this.hideContent)
    }

    if (this.type === 'popover') {
      target.addEventListener('pointerdown', this.toggleContent)
    }
  }

  /**
   * Verifica se um elemento HTML é focável.
   * @param element HTMLElement a ser verificado
   * @returns boolean
   */
  private isFocusable(element: HTMLElement): boolean {
    if (element.nodeType !== 1) return false

    // Verifica o atributo tabindex
    if (element.hasAttribute('tabindex')) {
      const tabIndex = element.getAttribute('tabindex')

      if (tabIndex === null) {
        return false
      }

      const index = parseInt(tabIndex, 10)
      return !isNaN(index) && index >= 0
    }

    // Verifica elementos interativos padrão
    const tag = element.tagName.toLowerCase()
    const interactiveTags = ['button', 'input', 'select', 'textarea', 'a']

    if (interactiveTags.includes(tag)) {
      if (tag === 'a') return element.hasAttribute('href')

      if (
        element instanceof HTMLButtonElement ||
        element instanceof HTMLInputElement ||
        element instanceof HTMLSelectElement ||
        element instanceof HTMLTextAreaElement
      ) {
        return !element.disabled
      }

      return false
    }

    return false
  }

  /**
   * Manipula a mudança no slot do trigger para verificar se há conteúdo focável.
   * @param event Evento de mudança do slot
   */
  private handleTriggerSlotChange = (event: Event) => {
    const slot = event.target as HTMLSlotElement
    const assignedNodes = slot.assignedNodes({ flatten: true })

    this.hasFocusableTrigger = assignedNodes.some((node) => {
      return node.nodeType === 1 && this.isFocusable(node as HTMLElement)
    })
  }

  /**
   * Controla a exibição do tooltip.
   * Quando o tooltip é exibido, ele é posicionado em relação ao elemento acionador.
   * Isso é feito utilizando a biblioteca Floating UI, que calcula a posição ideal do tooltip com base no espaço disponível na tela.
   * A função `autoUpdate` é utilizada para atualizar automaticamente a posição do tooltip quando o elemento acionador é movido ou redimensionado.
   */
  private positionTooltip() {
    const trigger = this.getPositionReferenceElement()

    if (trigger == null || this.contentElement == null) return

    this.floatingManager = new FloatingUIManager({
      triggerElement: trigger,
      contentElement: this.contentElement,
      arrowElement: this.arrowElement ?? undefined,
      placement: this.position,
    })

    this.floatingManager.start()
  }

  private cleanupPositioning() {
    this.floatingManager?.stop()
  }

  /**
   * Define os eventos de interação do tooltip.
   * Os eventos de interação incluem mouseenter, mouseleave, focus e blur.
   * @returns void
   */
  private setupInteractions() {
    const trigger = this.getPositionReferenceElement()

    if (trigger == null || this.contentElement == null) return

    this.removeTriggerInteractions(this.interactionTarget)
    this.addTriggerInteractions(trigger)
    this.interactionTarget = trigger

    if (this.isAutoVisible) {
      requestAnimationFrame(() => {
        this.clearShowTimer()
        this.setShowTimer()
      })
    }

    // Verifica inicialmente se o slot já tem elementos (caso slotchange não dispare no load inicial dependendo do framework)
    // Pequeno delay para garantir que o DOM light foi projetado
    requestAnimationFrame(() => {
      if (this.externalTriggerElement === null && this.triggerElement !== null) {
        const slot = this.triggerElement.querySelector('slot[name="trigger"]') as HTMLSlotElement | null

        if (slot !== null) {
          // Dispara manualmente a verificação inicial
          this.handleTriggerSlotChange({ target: slot } as unknown as Event)
        }
      } else {
        this.hasFocusableTrigger = true
      }
    })
  }

  /**
   * Alterna a exibição do tooltip.
   * Se o tooltip estiver visível, ele será ocultado.
   * Se o tooltip estiver oculto, ele será exibido.
   */
  private toggleContent = () => {
    this.fromPointerDown = true

    if (this.isVisible) {
      this.hideContent()
    } else {
      this.showContent()
    }
  }

  /**
   * Processa as ações relacionadas à exibição do tooltip.
   * Isso inclui exibir o tooltip e iniciar o tempo limite de ocultação automática.
   * @returns void
   */
  private showContent = () => {
    if (this.contentElement == null) return

    this.isVisible = true

    if (this.isAutoVisible) {
      if (this.hideDelay !== null) {
        this.clearHideTimer()
        this.setHideTimer()
      }
    }

    if (this.type === 'popover') {
      document.addEventListener('click', this.handleOutsideClick)
    }
  }

  /**
   * Processa as ações relacionadas ao fechamento do tooltip.
   * Isso inclui ocultar o tooltip e limpar o tempo limite de ocultação automática.
   * @returns void
   */
  private hideContent = () => {
    if (this.contentElement == null) return

    this.isVisible = false

    if (this.type === 'popover') {
      document.removeEventListener('click', this.handleOutsideClick)
      requestAnimationFrame(() => this.contentElement?.focus())
    }
  }

  /**
   * Configura o tempo limite para exibir o tooltip automaticamente.
   * O tempo limite é definido pelo valor da propriedade `showDelay`.
   */
  private setShowTimer() {
    this.showTimer = setTimeout(() => {
      this.showContent()
    }, this.showDelay || 0)
  }

  /**
   * Limpa o tempo limite de exibição automática do tooltip.
   */
  private clearShowTimer() {
    if (this.showTimer !== null) {
      clearTimeout(this.showTimer)
      this.showTimer = null
    }
  }

  /**
   * Configura o tempo limite para ocultar o tooltip automaticamente.
   * O tempo limite é definido pelo valor da propriedade `hideDelay`.
   */
  private setHideTimer() {
    this.hideTimer = setTimeout(() => {
      this.hideContent()
    }, this.hideDelay ?? 0)
  }

  /**
   * Limpa o tempo limite de ocultação automática do tooltip.
   */
  private clearHideTimer() {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer)
      this.hideTimer = null
    }
  }

  /**
   * Lida com cliques fora do tooltip.
   * É utilizado para o tooltip do tipo 'popover'.
   * @param event MouseEvent
   */
  private handleOutsideClick = (event: MouseEvent) => {
    const trigger = this.getPositionReferenceElement()
    const path = event.composedPath()

    if (
      this.isVisible &&
      this.type === 'popover' &&
      this.contentElement !== null &&
      trigger !== null &&
      !path.includes(this.contentElement) &&
      !path.includes(trigger)
    ) {
      this.hideContent()
    }
  }

  /**
   * Determina o mapeamento de classes CSS do componente.
   * @returns Um objeto que representa o mapeamento de classes CSS do componente.
   */
  private getCssClassMap(): CssClassMap {
    return {
      'br-tooltip': true,
    }
  }

  /**
   * Determina o mapeamento de classes CSS do trigger.
   * @returns Um objeto que representa o mapeamento de classes CSS do trigger.
   */
  private getTriggerCssClassMap(): CssClassMap {
    return {
      trigger: true,
    }
  }

  /**
   * Determina o mapeamento de classes CSS do conteúdo.
   * @returns Um objeto que representa o mapeamento de classes CSS do conteúdo.
   */
  private getContentCssClassMap(): CssClassMap {
    return {
      'is-visible': this.isVisible,
      content: true,
      popover: this.type === 'popover',
      large: this.density === 'large',
      medium: this.density === 'medium',
      small: this.density === 'small',
      info: this.state === 'info',
      warning: this.state === 'warning',
      error: this.state === 'error' || this.state === 'danger',
      danger: this.state === 'danger',
      success: this.state === 'success',
    }
  }

  /**
   * Determina o mapeamento de classes CSS da seta.
   * @returns Um objeto que representa o mapeamento de classes CSS da seta.
   */
  private getArrowCssClassMap(): CssClassMap {
    return {
      arrow: true,
      large: this.density === 'large',
      medium: this.density === 'medium',
      small: this.density === 'small',
      info: this.state === 'info',
      warning: this.state === 'warning',
      error: this.state === 'error' || this.state === 'danger',
      danger: this.state === 'danger',
      success: this.state === 'success',
    }
  }

  /**
   * Método do ciclo de vida do componente.
   * Este método é chamado uma única vez após o componente ser montado no DOM.
   */
  componentDidLoad() {
    if (typeof window === 'undefined') return
    this.setupInteractions()
  }

  /**
   * Método do ciclo de vida do componente.
   * Este método é chamado quando o componente é desconectado do DOM.
   */
  disconnectedCallback() {
    this.removeTriggerInteractions(this.interactionTarget)

    if (this.type === 'popover') {
      document.removeEventListener('click', this.handleOutsideClick)
    }

    this.cleanupPositioning()

    this.clearShowTimer()
    this.clearHideTimer()
  }

  /**
   * Determina o texto de acessibilidade baseado no estado do tooltip.
   */
  private getAccessibilityLabel(): string {
    const labels = {
      info: 'Informação:',
      warning: 'Aviso:',
      error: 'Erro:',
      danger: 'Erro:',
      success: 'Sucesso:',
    }
    return labels[this.state] || ''
  }

  /**
   * Renderiza o componente.
   * Este método é responsável por gerar o conteúdo HTML do componente.
   * Ele utiliza a função `h` do Stencil para criar elementos JSX.
   * @returns JSX.Element
   */
  render() {
    return (
      <Host>
        <div id={this.customId} class={this.getCssClassMap()}>
          <div
            class={this.getTriggerCssClassMap()}
            ref={(el) => (this.triggerElement = el as HTMLDivElement)}
            aria-describedby={`${this.customId}-content`}
            aria-expanded={this.isVisible ? 'true' : 'false'}
            aria-controls={`${this.customId}-content`}
            tabindex={this.externalTriggerElement !== null || this.hasFocusableTrigger ? -1 : 0}
          >
            <slot name="trigger" onSlotchange={this.handleTriggerSlotChange}></slot>
          </div>
          <div
            id={`${this.customId}-content`}
            class={this.getContentCssClassMap()}
            ref={(el) => (this.contentElement = el as HTMLDivElement)}
            role={this.type === 'popover' ? 'dialog' : 'tooltip'}
            tabindex="-1"
          >
            <span class="sr-only">{this.getAccessibilityLabel()}</span>
            <slot name="content"></slot>
            {this.type === 'popover' && (
              <br-button
                class="close"
                shape="circle"
                density="small"
                onClick={this.hideContent}
                aria-label="Fechar popover"
              >
                <br-icon icon-name="fa6-solid:xmark"></br-icon>
              </br-button>
            )}
            <div class={this.getArrowCssClassMap()} ref={(el) => (this.arrowElement = el as HTMLDivElement)}></div>
          </div>
        </div>
      </Host>
    )
  }
}
